import React, { useState } from 'react';
import { MapPin, Bell, Check } from 'react-feather';
import { subscribeToPush, isPushSupported } from '../services/pushService';
import { logger } from '../lib/logger';
import './PermissionsSetup.css';

const log = logger.createNamespace('PermissionsSetup');

interface PermissionsSetupProps {
  onComplete: () => void;
  onSkip?: () => void;
}

// Reusable Stepper Component
const Stepper: React.FC = () => (
  <div className="stepper">
    <div className="stepper-step completed">
      <div className="stepper-dot"><Check size={14} /></div>
      <span className="stepper-label">Welcome</span>
    </div>
    <div className="stepper-line completed" />
    <div className="stepper-step completed">
      <div className="stepper-dot"><Check size={14} /></div>
      <span className="stepper-label">Zone</span>
    </div>
    <div className="stepper-line completed" />
    <div className="stepper-step active">
      <div className="stepper-dot">3</div>
      <span className="stepper-label">Setup</span>
    </div>
  </div>
);

export const PermissionsSetup: React.FC<PermissionsSetupProps> = ({ onComplete, onSkip }) => {
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationBlocked, setNotificationBlocked] = useState(false);
  const [pushServiceUnavailable, setPushServiceUnavailable] = useState(false);

  const handleLocationPermission = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setLocationEnabled(checked);

    if (checked && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(() => {}, () => {});
    }
  };

  const handleNotificationPermission = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setNotificationsEnabled(checked);

    if (!checked) return;

    if (!('Notification' in window)) {
      log.warn('Notification API not available in this browser');
      return;
    }

    const currentPermission = Notification.permission;
    log.info('Notification permission state', { currentPermission });

    if (currentPermission === 'denied') {
      log.warn('Notification permission was previously denied — user must reset it in browser settings');
      setNotificationBlocked(true);
      return;
    }

    Notification.requestPermission().then((permission) => {
      log.info('Notification permission result', { permission });
      if (permission === 'granted' && isPushSupported()) {
        setPushServiceUnavailable(false);
        subscribeToPush()
          .then((result) => {
            if (!result.ok && result.reason === 'push_service_error') {
              setPushServiceUnavailable(true);
            }
          })
          .catch(() => {});
      }
    }).catch((err) => {
      log.error('Notification.requestPermission() threw', err);
    });
  };

  const isSetupComplete = locationEnabled && notificationsEnabled;

  return (
    <div className="permissions-content">
      {/* Stepper */}
      <div className="onboarding-header">
        <Stepper />
      </div>

      {/* Main Content */}
      <div className="permissions-main">
        <div className="permissions-hero">
          <h1 className="setup-title">Final Setup</h1>
          <p className="setup-description">
            Enable these features to get the most out of TowApp
          </p>
        </div>

        <div className="permissions-list">
          <div className={`permission-card ${locationEnabled ? 'active' : ''}`}>
            <div className="permission-header">
              <div className="permission-icon">
                <MapPin size={22} />
              </div>
              <div className="permission-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={locationEnabled}
                    onChange={handleLocationPermission}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="permission-content">
              <h3>Location Services</h3>
              <p>Find incidents near you and sort by distance</p>
            </div>
          </div>

          <div className={`permission-card ${notificationsEnabled ? 'active' : ''}`}>
            <div className="permission-header">
              <div className="permission-icon">
                <Bell size={22} />
              </div>
              <div className="permission-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={handleNotificationPermission}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div className="permission-content">
              <h3>Push Notifications</h3>
              <p>Get instant alerts for incidents in your zones</p>
              {notificationBlocked && (
                <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Notifications were previously blocked. Click the lock icon in your
                  browser's address bar, set Notifications to "Allow", then reload.
                </p>
              )}
              {pushServiceUnavailable && (
                <p style={{ color: '#f39c12', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Push couldn&apos;t be set up on this browser or network (common on localhost).
                  You can continue; try again on the deployed app or another device.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="permissions-footer">
        <button 
          className={`continue-btn ${isSetupComplete ? 'active' : ''}`}
          onClick={onComplete}
          disabled={!isSetupComplete}
        >
          {isSetupComplete ? 'Start Using TowApp' : 'Enable Both to Continue'}
        </button>

        {onSkip && (
          <button 
            className="skip-btn"
            onClick={onSkip}
          >
            Skip for now
          </button>
        )}

        <p className="privacy-text">
          You can change these settings anytime
        </p>
      </div>
    </div>
  );
};
